// src/components/Admin/AdminLayout.jsx
import React, { useEffect, useState } from "react";
import {
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";

// Helper to safely read user from localStorage
const getStoredUser = () => {
  try {
    const saved = localStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    console.error("Error reading user from localStorage in AdminLayout:", e);
    return null;
  }
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(() => getStoredUser());

  // Keep user in sync if localStorage changes in another tab
  useEffect(() => {
    const handleStorage = () => {
      setUser(getStoredUser());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    } catch (e) {
      console.warn("Failed to clear auth data on logout:", e);
    }

    setUser(null);
    // Go back to main public site
    navigate("/", { replace: true });
  };

  const handleBackToSite = () => {
    navigate("/", { replace: true });
  };

  const isActive = (path) => {
    // helper for highlighting active sidebar links
    if (path === "/admin" && location.pathname === "/admin") return true;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="admin-layout" style={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: "260px",
          backgroundColor: "#111827",
          color: "#e5e7eb",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Brand + Back button */}
        <div
          style={{
            padding: "20px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  backgroundColor: "#4f46e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                D
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                  DharmaAnswers
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    opacity: 0.7,
                  }}
                >
                  Admin Panel
                </div>
              </div>
            </div>
          </div>

          {/* Back to main site */}
          <button
            type="button"
            onClick={handleBackToSite}
            style={{
              marginTop: 4,
              width: "100%",
              padding: "8px 10px",
              fontSize: "0.8rem",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.15)",
              backgroundColor: "transparent",
              color: "#e5e7eb",
              cursor: "pointer",
            }}
          >
            ← Back to DharmaAnswers
          </button>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: "16px 12px" }}>
          <div style={{ marginBottom: 8, fontSize: "0.75rem", opacity: 0.7 }}>
            MAIN
          </div>

          <NavLink
            to="/admin"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin") ? "#1f2937" : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            Dashboard
          </NavLink>

          <NavLink
            to="/admin/users"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin/users") ? "#1f2937" : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            Users
          </NavLink>

          <div
            style={{
              marginTop: 16,
              marginBottom: 8,
              fontSize: "0.75rem",
              opacity: 0.7,
            }}
          >
            ASK SHIA
          </div>

          <NavLink
            to="/admin/questions/pending"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin/questions/pending")
                ? "#1f2937"
                : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            Pending
          </NavLink>

          <NavLink
            to="/admin/questions/answered"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin/questions/answered")
                ? "#1f2937"
                : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            Answered
          </NavLink>

          <NavLink
            to="/admin/questions"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin/questions")
                ? "#1f2937"
                : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            All Questions
          </NavLink>

          <div
            style={{
              marginTop: 16,
              marginBottom: 8,
              fontSize: "0.75rem",
              opacity: 0.7,
            }}
          >
            ANALYTICS
          </div>

          <NavLink
            to="/admin/search-history"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin/search-history")
                ? "#1f2937"
                : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            Search History
          </NavLink>

          <NavLink
            to="/admin/activity-logs"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin/activity-logs")
                ? "#1f2937"
                : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            Activity Logs
          </NavLink>

          <NavLink
            to="/admin/feedback"
            style={{
              display: "block",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 4,
              backgroundColor: isActive("/admin/feedback")
                ? "#1f2937"
                : "transparent",
              textDecoration: "none",
              color: "inherit",
              fontSize: "0.9rem",
            }}
          >
            Feedback
          </NavLink>
        </nav>

        {/* Bottom user + logout */}
        <div
          style={{
            padding: "14px 12px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div style={{ marginBottom: 8, fontSize: "0.8rem" }}>
            <div style={{ fontWeight: 600 }}>
              {user?.name || "Admin User"}
            </div>
            <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
              {user?.email || "admin@dharmaanswers.com"}
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "#4f46e5",
              color: "#f9fafb",
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Main content (dashboard pages) */}
      <main style={{ flex: 1, backgroundColor: "#f9fafb" }}>
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
